import "./index.scss";
import { useContext, useEffect, useMemo, useState } from "react";
import { ProjectContext } from "context/ProjectProvider";
import {
  getActivitiesModifiedCount,
  getDashBoardDataV2,
  getMaterialChangelog
} from "services/auth";
import { useHistory, useParams } from "react-router";
import { AppContext } from "context/AppProvider";
import {
  Button,
  Checkbox,
  ConfigProvider,
  Radio,
  RadioChangeEvent,
  Segmented,
  Select
} from "antd";
import { embedDashboard } from "@superset-ui/embedded-sdk";
import { client } from "services/axios";
import { BASE_URL } from "config/config";
import WelcomeStartupSetup from "components/welcome-startup-setup";
import ErrorBoundary from "components/error-boundary";
import { TOUR_READY } from "components/tour";
import { supersetEnv } from "utils/utils";
import ActionItems from "./action-items";

import RiskUpdateComponent from "../risk-update";
import AlertDashboard from "./alert/alert-dashboard";
import MaterialUpdatesDashboard, {
  MaterialUpdateItem
} from "./material-updates/material-updates-dashboard";
import PlanningDataQualityReport from "./data-quality-report";
import {
  FilterNamesEnum,
  GcDashBoardType,
  NavigationPageEnum,
  EDashboardType
} from "../models";
import ProjectTimeline from "./time-line";
import AiInsightDashboard from "./ai-insight/ai-insight-dashboard";

// embedNo/exportNo are the backend's `dashboard_no`; it maps them to the
// per-environment Superset dashboard id and mints the matching guest token.
// No exportNo means there is no print-formatted copy, so no export button.
type TAnalyticsDashboard = {
  key: string;
  label: string;
  flag: string;
  containerId: string;
  embedNo: number;
  exportNo?: number;
};

// Options are listed alphabetically, but this one opens by default wherever
// the project has it.
const DEFAULT_DASHBOARD_FLAG = "MATERIAL_PROGRESS_DASHBOARD";

const ANALYTICS_DASHBOARDS: TAnalyticsDashboard[] = [
  {
    key: "1",
    label: "Material Progress",
    flag: "MATERIAL_PROGRESS_DASHBOARD",
    containerId: "material-progress-dashboard-container",
    embedNo: 1,
    exportNo: 7
  },
  {
    key: "2",
    label: "Submittal Progress",
    flag: "SUBMITTAL_PROGRESS_DASHBOARD",
    containerId: "submittal-progress-dashboard-container",
    embedNo: 6,
    exportNo: 11
  },
  {
    key: "3",
    label: "Material Readiness",
    flag: "MATERIAL_READINESS_DASHBOARD",
    containerId: "material-readiness-dashboard-container",
    embedNo: 4,
    exportNo: 9
  },
  {
    key: "4",
    label: "Design Review Load",
    flag: "DESIGN_REVIEW_QUEUE_FORECAST_DASHBOARD",
    containerId: "design-review-load-dashboard-container",
    embedNo: 12
  }
];

function GCProjectDashboard2() {
  const history = useHistory();
  const { projectId } = useParams() as any;
  const {
    tokenRetrievalState,
    projectDetails,
    projectToken,
    isFeatureFlagEnabled,
    lastImportLogData,
    isProjectArchived
  } = useContext(ProjectContext);

  const {
    setSubmittalListFilter,
    setMaterialListFilter,
    setBidPackageListFilter
  } = useContext(AppContext);
  const isMaterialProgressDashboardEnabled = isFeatureFlagEnabled(
    "MATERIAL_PROGRESS_DASHBOARD"
  );
  const isMaterialReadinessDashboardEnabled = isFeatureFlagEnabled(
    "MATERIAL_READINESS_DASHBOARD"
  );
  const isProcurementScheduleEnabled = isFeatureFlagEnabled(
    "PROCUREMENT_SCHEDULE"
  );
  const isSubmittalProgressDashboardEnabled = isFeatureFlagEnabled(
    "SUBMITTAL_PROGRESS_DASHBOARD"
  );
  const isDesignReviewQueueForecastDashboardEnabled = isFeatureFlagEnabled(
    "DESIGN_REVIEW_QUEUE_FORECAST_DASHBOARD"
  );

  const isAiMaterialInsightsDashboardEnabled = isFeatureFlagEnabled(
    "AI_MATERIAL_INSIGHTS"
  );

  const isMaterialChangelogEnabled = isFeatureFlagEnabled("MATERIAL_CHANGELOG");

  const [dashboardData, setDashboardData] = useState<GcDashBoardType>();
  const [activitiesModifiedCount, setactivitiesModifiedCount] = useState();
  const [materialChangelog, setMaterialChangelog] = useState<
    MaterialUpdateItem[] | undefined
  >();
  const [selectedKey, setSelectedKey] = useState<string>();
  const [activeDashboard, setActiveDashboard] = useState<EDashboardType>(
    EDashboardType.Overview
  );

  const goToFiltered = (
    page: NavigationPageEnum,
    filter: FilterNamesEnum,
    value = undefined
  ) => {
    switch (page) {
      case NavigationPageEnum.MaterialLog:
        setMaterialListFilter({ key: filter.toString(), value });
        history.push("./materials");
        break;
      case NavigationPageEnum.SubmittalLog:
        setSubmittalListFilter({ key: filter.toString(), value });
        history.push("./submittals");
        break;
      case NavigationPageEnum.BidPackageLog:
        setBidPackageListFilter({ key: filter.toString(), value });
        history.push("./bid-packages");
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const getDashBoardFuc = async () => {
      const response = await getDashBoardDataV2(tokenRetrievalState.token, {});

      if (response.success) {
        const { data } = response;
        setDashboardData(data.success);
      } else {
        console.error(response.error);
      }
    };

    const getActivitiesModified = async () => {
      const response = await getActivitiesModifiedCount(
        tokenRetrievalState.token,
        {}
      );
      if (response.success) {
        const { data } = response;
        setactivitiesModifiedCount(data.success);
      } else {
        console.error(response.error);
      }
    };

    const getMaterialChangelogData = async () => {
      const response = await getMaterialChangelog(
        tokenRetrievalState.token,
        {}
      );
      if (response.success) {
        const { data } = response;
        setMaterialChangelog(data.success?.material_changelog ?? []);
      } else {
        console.error(response.error);
      }
    };

    if (tokenRetrievalState?.token && projectDetails) {
      getDashBoardFuc();
      getActivitiesModified();
      getMaterialChangelogData();
    }
  }, [tokenRetrievalState, projectDetails]);

  const availableDashboards = useMemo(() => {
    const enabledByFlag: Record<string, boolean> = {
      MATERIAL_PROGRESS_DASHBOARD: isMaterialProgressDashboardEnabled,
      SUBMITTAL_PROGRESS_DASHBOARD: isSubmittalProgressDashboardEnabled,
      MATERIAL_READINESS_DASHBOARD: isMaterialReadinessDashboardEnabled,
      DESIGN_REVIEW_QUEUE_FORECAST_DASHBOARD:
        isDesignReviewQueueForecastDashboardEnabled
    };
    return ANALYTICS_DASHBOARDS.filter((d) => enabledByFlag[d.flag]).sort(
      (a, b) => a.label.localeCompare(b.label)
    );
  }, [
    isMaterialProgressDashboardEnabled,
    isMaterialReadinessDashboardEnabled,
    isSubmittalProgressDashboardEnabled,
    isDesignReviewQueueForecastDashboardEnabled
  ]);

  // Derived rather than stored-and-corrected, so the default always follows
  // the current list instead of sticking to a key written during an earlier
  // render where fewer dashboards were available.
  const selectedDashboard =
    availableDashboards.find((d) => d.key === selectedKey) ??
    availableDashboards.find((d) => d.flag === DEFAULT_DASHBOARD_FLAG) ??
    availableDashboards[0];

  useEffect(() => {
    if (activeDashboard !== EDashboardType.Analytics || !selectedDashboard) {
      return;
    }

    const container = document.getElementById(selectedDashboard.containerId);
    if (!container) {
      console.error("Dashboard container not found.");
      return;
    }

    // Every container stays mounted once embedded, so a populated one is an
    // already-running dashboard - re-embedding would refetch a guest token.
    if (container.hasChildNodes()) return;

    const embedSelectedDashboard = async () => {
      try {
        const tokens = await client.post(
          `${BASE_URL}/superset/dashboard`,
          {
            dashboard_no: selectedDashboard.embedNo
          },
          {
            headers: {
              Authorization: `Bearer ${projectToken}`,
              "Content-Type": "application/json"
            }
          }
        );

        embedDashboard({
          id: tokens.data.dashboard_id,
          supersetDomain: supersetEnv(),
          mountPoint: container,
          fetchGuestToken: () => Promise.resolve(tokens.data.token),
          dashboardUiConfig: {
            hideTitle: true,
            hideChartControls: false,
            hideTab: true
          }
        });
      } catch (error) {
        console.error("Error posting data:", error);
        throw error;
      }
    };

    embedSelectedDashboard();
  }, [activeDashboard, projectToken, selectedDashboard]);

  const segmentOptions = useMemo(() => {
    const options: Array<{ label: React.ReactNode; value: EDashboardType }> = [
      { label: "Overview", value: EDashboardType.Overview }
    ];
    if (availableDashboards.length > 0)
      options.push({
        label: (
          <div className="relative inline-flex items-center">Analytics</div>
        ),
        value: EDashboardType.Analytics
      });
    if (isProcurementScheduleEnabled)
      options.push({ label: "Timeline", value: EDashboardType.Timeline });
    return options;
  }, [isProcurementScheduleEnabled, availableDashboards]);

  const handleExport = () => {
    if (selectedDashboard?.exportNo === undefined) return;
    window.open(
      `/project/${projectId}/analytics/export?dashboard_no=${selectedDashboard.exportNo}`,
      "_blank"
    );
  };

  const [zoomSelection, setZoomSelection] = useState<string>("Quarter");
  const handleZoomChange = (e: RadioChangeEvent) => {
    const value = e.target.value as string;
    setZoomSelection(value);
  };

  return (
    <div
      className="flex flex-col h-full bg-white gc-project-dashboard"
      data-tour-ready={dashboardData ? TOUR_READY.GC_DASHBOARD : undefined}
    >
      <div className="flex w-full py-2 px-6 font-medium items-center justify-between ">
        <div className="flex items-center">
          <div className="font-semibold text-xl pr-3">Dashboards</div>

          {segmentOptions.length > 1 && (
            // antd v6 regression: `shape="round"` makes the track/items pill-
            // shaped, and v6 adds a grey item hover/active background fill
            // (itemHoverBg/itemActiveBg) that highlights the whole section on
            // hover. The v4 look was a rectangular track where only the text
            // darkened on hover. Drop the round shape and null out the hover/
            // active bg tokens so only itemHoverColor (near-black text) remains.
            <ConfigProvider
              theme={{
                components: {
                  Segmented: {
                    itemHoverBg: "transparent",
                    itemActiveBg: "transparent"
                  }
                }
              }}
            >
              <Segmented
                size="large"
                options={segmentOptions}
                onChange={(value) => {
                  // string
                  setActiveDashboard(value as EDashboardType);
                }}
                data-testid="gc-project-dashboard-segmented"
              />
            </ConfigProvider>
          )}
        </div>
        {activeDashboard === EDashboardType.Analytics &&
          selectedDashboard?.exportNo !== undefined && (
            <Button
              type="primary"
              onClick={handleExport}
              disabled={!!isProjectArchived}
            >
              Export PDF/Print
            </Button>
          )}
        {activeDashboard === EDashboardType.Timeline && (
          <ErrorBoundary>
            <div className="flex items-center space-x-2">
              <div className="flex space-x-2 items-center bg-ciq-gray-100 bg-opacity-40 rounded px-4 py-2">
                <Checkbox
                  className="filterCheckbox font-normal"
                  name="HIGH"
                  data-testid="high-risk-checkbox"
                >
                  Filter High Risk
                </Checkbox>
              </div>
              <Radio.Group
                options={[
                  { value: "Months", label: "Months" },
                  { value: "Quarter", label: "Quarter" },
                  { value: "Year", label: "Year" }
                ]}
                value={zoomSelection}
                optionType="button"
                onChange={handleZoomChange}
                data-testid="timeline-zoom-radio"
              />
            </div>
          </ErrorBoundary>
        )}
      </div>
      {activeDashboard === EDashboardType.Overview && (
        <div className="w-full bg-white flex px-5 py-2 mb-8 hideScroll">
          <div className="h-full grow pr-5 space-y-5">
            <ErrorBoundary>
              <ActionItems
                projectId={projectId}
                dashboardData={dashboardData}
                projectDetails={projectDetails}
                goToFiltered={goToFiltered}
              />
            </ErrorBoundary>
            <ErrorBoundary>
              <RiskUpdateComponent
                dashboardData={dashboardData}
                goToFiltered={goToFiltered}
              />
            </ErrorBoundary>
          </div>
          <div className="h-full w-[500px]">
            {isAiMaterialInsightsDashboardEnabled &&
              dashboardData?.ai_insights?.materials && (
                <div className="pb-10">
                  <ErrorBoundary>
                    <AiInsightDashboard
                      ai_insightsMaterials={dashboardData.ai_insights.materials}
                      goToFiltered={goToFiltered}
                    />
                  </ErrorBoundary>
                </div>
              )}
            <div className="">
              <ErrorBoundary>
                <AlertDashboard
                  projectId={projectId}
                  activities_modified={activitiesModifiedCount}
                  alerts={dashboardData?.alerts}
                  goToFiltered={goToFiltered}
                  lastSyncDateTime={lastImportLogData?.lastSyncDateTime}
                />
              </ErrorBoundary>
            </div>
            <div className="pt-10">
              <ErrorBoundary>
                <PlanningDataQualityReport
                  dashboardData={dashboardData}
                  goToFiltered={goToFiltered}
                />
              </ErrorBoundary>
            </div>
            {isMaterialChangelogEnabled && (
              <div className="pt-10">
                <ErrorBoundary>
                  <MaterialUpdatesDashboard materials={materialChangelog} />
                </ErrorBoundary>
              </div>
            )}
          </div>
        </div>
      )}
      {activeDashboard === EDashboardType.Analytics &&
        availableDashboards.length > 0 && (
          <ErrorBoundary>
            <div className="flex items-center gap-3 px-6 py-2 border-0 border-b border-solid border-ciq-gray-200">
              <span className="text-ciq-gray-700">View</span>
              <Select
                data-testid="analytics-dashboard-select"
                className="min-w-[240px]"
                value={selectedDashboard?.key}
                onChange={(value) => setSelectedKey(value)}
                options={availableDashboards.map((d) => ({
                  label: d.label,
                  value: d.key
                }))}
                popupMatchSelectWidth={false}
              />
            </div>
            <div className="w-full px-6 pt-3">
              {availableDashboards.map((d) => (
                <div
                  key={d.key}
                  id={d.containerId}
                  className={`w-full hideScroll !h-[73vh] ${
                    d.key === selectedDashboard?.key ? "" : "hidden"
                  }`}
                />
              ))}
            </div>
          </ErrorBoundary>
        )}

      {activeDashboard === EDashboardType.Timeline && (
        <ErrorBoundary>
          <ProjectTimeline zoomSelection={zoomSelection} />
        </ErrorBoundary>
      )}
      <WelcomeStartupSetup />
    </div>
  );
}
export default GCProjectDashboard2;
